<template>
	<v-expansion-panel v-if="event && !isDroppedByETM" class="playlist-event"
		rounded="0"
		elevation="0"
		:class="{
			'now-playing': isNowPlaying,
			'spot-block': isSpotBlock,
			'local-break': isLocalBreak,
			'cueingAsNext': cueingAsNext,
			'voice-track': isVoiceTrack
		}">
		<v-expansion-panel-title expand-icon=""
			hide-actions
			:readonly="!(isSpotBlock || isLocalBreak || isDVTBlock) || event.empty"
			:style="{'cursor': `${(isSpotBlock || isLocalBreak || isDVTBlock) ? 'pointer' : 'default'}`}"
			@mouseover="() => {
				if (!isDVTBlock) {
					targetIndicatorIsVisible = true;
					hoverToPaste = true;
				}
			}"
			@mouseleave="() => {
				targetIndicatorIsVisible = false;
				hoverToPaste = false;
			}">
			<v-hover v-slot="{ props }">
				<v-card v-bind="props" v-if="!event.empty"
					variant="outlined"
					rounded="0"
					width="100%"
					ref="rootCard"
					:class="{
						'disabled-event': isDisabledEvent && !isMissingAudio && !unresolvedTag,
						'skipped-event': skippedEvent,
						'missing-audio': isMissingAudio || unresolvedTag,
						'unrecorded-vt': unrecordedVoiceTrack
					}"
					@dblclick.prevent="openAssetManagement"
					@contextmenu.prevent="openContextMenu">
					<div class="asset-container d-flex align-center" :class="{'now-playing': isNowPlaying}">

						<!-- Pot Number -->
						<v-tooltip location="bottom" :disabled="!potNumber" text="Pot Number">
							<template v-slot:activator="{ props }">
								<div v-bind="props" class="pot-number"
									:class="{'empty': !asset || !potNumber, 'text-h6': potNumber > 9}">
									{{ potNumber }}
								</div>
							</template>
						</v-tooltip>

						<!-- Start Time and Now Playing label -->
						<div class="d-flex flex-column airtime pl-2">
							<div v-if="isNowPlaying" class="now-playing-label">
								Now Playing
								<v-icon size="18" class="ml-1" icon="mdi-volume-high"/>
							</div>
							<v-tooltip location="bottom" v-if="displayAirTime" text="Airtime">
								<template v-slot:activator="{ props }">
									<div v-bind="props" class="event-field"
										:class="[isNowPlaying ? 'text-h3' : 'text-body-1']"
										:style="missingAudioPath ? 'color:white' : ''">
										{{ airTime }}
									</div>
								</template>
							</v-tooltip>
						</div>

						<!-- Chain Type -->
						<div class="chain-type d-flex align-center justify-center">
							<div v-if="isPartiallyDroppedByETM">
								<v-tooltip location="bottom">
									<template v-slot:activator="{ props }">
										<v-icon v-bind="props" size="25" icon="mdi-cancel"
											:color="isPartiallyDroppedByETM ? '#F7CE03' : '#484C4F'"/>
									</template>
									<span>
										{{ "Event will end early to meet next ETM" }}
									</span>
								</v-tooltip>
							</div>

							<div v-if="chainTypeIsStop" class="event-field">
								<v-icon size="25" color="#C6002B" icon="mdi-stop-circle"/>
							</div>

							<div v-if="(isPlayAsset || isSpotBlock) && chainType" class="event-field">
								<v-menu close-on-content-click>
									<template v-slot:activator="{ props }">
										<v-btn v-bind="props" icon variant="text" density="compact" size="30"
											:disabled="chainTypeUpdating || skippedEvent || isDisabledEvent">
											<Image :img="chainTypeIcon" width="24" height="24"/>
										</v-btn>
									</template>
									<v-list>
										<v-list-item v-for="type in supportedChainTypes" :key="type.value"
											class="pl-2" @click="updateChainType(type.value)">
											<template v-slot:prepend>
												<v-avatar rounded="0" size="32px" class="ma-0">
													<Image :img="`${type.icon}.svg`" width="24" height="24"/>
												</v-avatar>
											</template>
											<v-list-item-title class="text-body-2"
												:class="{ 'font-weight-bold': event.Chain === type.value}">
												{{ type.description }}
											</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</div>

						<!-- Event Info -->
						<div class="event-field text-truncate"
							:class="[isNowPlaying || isETM || isModeSelectionEvent ? 'text-h3': 'text-body-1']">

							<!-- Asset -->
							<div v-if="isPlayAsset">
								<!-- Asset Info (Icon / Album / Title - Artist) -->
								<div v-if="asset && !missingAudioPath" class="d-flex align-center text-truncate">
									<v-menu v-if="!isLiveRead"
										v-model="songDetailsPopup.open"
										:open-on-hover="true"
										:close-on-content-click="false"
										content-class="song-details-popup">
										<template v-slot:activator="{ props }">
											<v-icon v-bind="props" class="asset-type-icon" :icon="assetTypeIcon"/>
										</template>
										<v-list density="compact" class="py-2">
											<v-expansion-panels @mouseleave="songDetailsPopup.open = false">
												<v-btn class="justify-start" variant="text"
													@click="copyToClipboard(event)">
													Copy Event JSON
												</v-btn>

												<v-expansion-panel elevation="0">
													<v-expansion-panel-title hide-actions :ripple="false">
														<template v-slot:default="{ expanded }">
															<v-list-item>
																<template v-slot:prepend>
																	<v-icon
																		:icon="`mdi-menu-${expanded ? 'down' : 'right'}`"/>
																</template>
																<span> Event Details </span>
															</v-list-item>
														</template>
													</v-expansion-panel-title>
													<v-expansion-panel-text>
														<v-list-item v-for="(item, key) in eventDetails" :key="key">
															<v-list-item-subtitle v-if="typeof item !== 'object'">
																{{ key }}: {{ item }}
															</v-list-item-subtitle>

															<v-list-item-subtitle v-else>
																{{ key }}: {{ item.value }}
																<v-btn icon class="ml-2"
																	variant="text"
																	density="compact"
																	color="#717277"
																	size="14"
																	@click="copyToClipboard(item.value)">
																	<v-icon v-if="item.copyButton"
																		icon="mdi-content-copy"
																		size="14"/>
																</v-btn>
															</v-list-item-subtitle>
														</v-list-item>
													</v-expansion-panel-text>
												</v-expansion-panel>

												<v-expansion-panel elevation="0">
													<v-expansion-panel-title hide-actions :ripple="false">
														<template v-slot:default="{ expanded }">
															<v-list-item>
																<template v-slot:prepend>
																	<v-icon
																		:icon="`mdi-menu-${expanded ? 'down' : 'right'}`"/>
																</template>
																<span> Asset Details </span>
															</v-list-item>
														</template>
													</v-expansion-panel-title>
													<v-expansion-panel-text>
														<v-list-item v-for="(item, key) in assetDetails" :key="key">
															<v-list-item-subtitle v-if="typeof item !== 'object'">
																{{ key }}: {{ item }}
															</v-list-item-subtitle>

															<v-list-item-subtitle v-else>
																{{ key }}: {{ item.value }}
																<v-btn icon class="ml-2"
																	variant="text"
																	density="compact"
																	color="#717277"
																	size="14"
																	@click="copyToClipboard(item.value)">
																	<v-icon v-if="item.copyButton"
																		icon="mdi-content-copy"
																		size="14"/>
																</v-btn>
															</v-list-item-subtitle>
														</v-list-item>
													</v-expansion-panel-text>
												</v-expansion-panel>
											</v-expansion-panels>
										</v-list>
									</v-menu>
									<div class="d-flex flex-column text-truncate">
										<div class="text-truncate">
											<v-tooltip location="bottom" text="Title">
												<template v-slot:activator="{ props }">
													<span v-bind="props">{{ asset.Title }}</span>
												</template>
											</v-tooltip>
											-
											<v-tooltip location="bottom" text="Artist">
												<template v-slot:activator="{ props }">
													<span v-bind="props">{{ asset.Artist }}</span>
												</template>
											</v-tooltip>
										</div>
										<v-tooltip v-if="asset.Album" location="bottom" text="Album">
											<template v-slot:activator="{ props }">
											<span v-bind="props" class="text-caption text-truncate">
												{{ asset.Album }}
											</span>
											</template>
										</v-tooltip>
									</div>
								</div>

								<!-- Unrecorded VoiceTrack -->
								<div v-if="unrecordedVoiceTrack" class="d-flex align-center text-truncate">
									<v-icon class="asset-type-icon" icon="mdi-alert"/>
									Unrecorded VoiceTrack
								</div>

								<!-- Missing Audio -->
								<div v-if="isMissingAudio" class="d-flex align-center">
									<v-tooltip location="bottom" text="Missing Audio">
										<template v-slot:activator="{ props }">
											<v-icon v-bind="props" icon="mdi-close-circle"/>
										</template>
									</v-tooltip>
									<v-tooltip location="bottom"
										:text="assetType?.Description ?? 'Unknown Asset Type'">
										<template v-slot:activator="{ props }">
											<v-icon v-bind="props" class="asset-type-icon" :icon="assetTypeIcon"/>
										</template>
									</v-tooltip>
									<v-tooltip location="bottom" text="Title">
										<template v-slot:activator="{ props }">
                                        <span v-bind="props" class="event-field text-truncate"
											:class="{'text-white': missingAudioPath}">
                                            {{ asset.Title }}
                                        </span>
										</template>
									</v-tooltip>
								</div>

								<!-- Unresolved Tag -->
								<div v-if="unresolvedTag" class="d-flex align-center">
									<v-tooltip location="bottom"
										:text="assetType?.Description ?? 'Unknown Asset Type'">
										<template v-slot:activator="{ props }">
											<v-icon v-bind="props" class="asset-type-icon" :icon="assetTypeIcon"/>
										</template>
									</v-tooltip>
									<v-tooltip location="bottom" :text="unresolvedTag">
										<template v-slot:activator="{ props }">
											<div v-bind="props" class="event-field text-truncate text-white">
												{{ "Unresolved Tags: " + unresolvedTag }}
											</div>
										</template>
									</v-tooltip>
								</div>
							</div>

							<!-- Spot Block -->
							<div v-if="isSpotBlock" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="spotBlockIcon"/>
								<div class="event-field ml-4 d-flex align-center">
									{{ type === "localBreakEvent" ? (isLocalSpotBlock ? "Local" : "Network") : "" }}
									{{
									event.ChildPlaylist?.TargetLength > 0 ?
									`Spot Block - ${secondsToFormat(
									event.ChildPlaylist?.TargetLength,
									"mm:ss",
									{trim: "left", stopTrim: "m"}
									)}` :
									"Spot Block"
									}}
									<v-icon class="ml-2 panel-arrow" icon="mdi-chevron-down"/>

									<div v-if="spotBlockContainsLiveRead" class="ml-4">
										<v-tooltip location="bottom">
											<template v-slot:activator="{ props }">
												<v-chip v-bind="props" label variant="outlined" class="live-read">
													LIVE READ
												</v-chip>
											</template>
											<span>There is a Live Read inside this Spotblock</span>
										</v-tooltip>
									</div>
								</div>
							</div>

							<!-- DVT Block -->
							<div v-if="isDVTBlock" class="d-flex align-center">
								<v-icon icon="mdi-forum" class="asset-type-icon"/>
								<div class="event-field ml-4 d-inline-flex">
									DVT Block
									<v-icon class="ml-2 panel-arrow" icon="mdi-chevron-down"/>
								</div>
							</div>

							<!-- Local Break -->
							<div v-if="isLocalBreak" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="localBreakIcon"/>
								<div class="event-field ml-4 d-inline-flex">
									Local Break
									<v-icon class="ml-2 panel-arrow" icon="mdi-chevron-down"/>
								</div>
							</div>

							<!-- Live Read -->
							<div v-if="isLiveRead" class="d-flex align-center">
								<div class="event-field d-inline-flex">
									{{
									event.LinearAdData.spotLength > 0 ?
									`${event.LinearAdData.displayName || event.LinearAdData.title ||
									event.LinearAdData.description} - ${secondsToFormat(
									event.LinearAdData.spotLength,
									"mm:ss",
									{trim: "left", stopTrim: "m"}
									)}` :
									`${event.LinearAdData.displayName || event.LinearAdData.title ||
									event.LinearAdData.description}`
									}}
								</div>
								<v-tooltip location="bottom">
									<template v-slot:activator="{ props }">
										<v-chip v-bind="props" label variant="outlined" class="live-read ml-4">
											LIVE READ
										</v-chip>
									</template>
									<span>Live Read</span>
								</v-tooltip>
							</div>

							<!-- Exact Time Marker -->
							<div v-if="isETM" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="iconETM"/>
								<span class="event-field text-truncate mx-4">
                                    Exact Time Marker
                                </span>
								<v-select :model-value="event.EtmType"
									:items="EtmTypes.getArrayValidItems()"
									class="d-flex align-end"
									variant="outlined"
									density="compact"
									hide-details
									:disabled="!editETMFeatureEnabled"
									@update:model-value="updateETMType(event.HardTime, $event)"
								/>
								<div v-if="gapTime" class="gap-time ml-10">
									Gap: {{ gapTime > 0 ? "+" : "" }}{{
									secondsToFormat(
									gapTime,
									"hh:mm:ss",
									{trim: "left", stopTrim: "m"}
									)
									}}
								</div>
							</div>

							<!-- Comment -->
							<div v-if="isComment" class="">
								{{ description }}
							</div>

							<!-- Rejoin -->
							<div v-if="isRejoin" class="ml-9">
								Rejoin
							</div>

							<!-- Mode Selection event -->
							<div v-if="isModeSelectionEvent" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="iconModeSelection"/>
								<span class="event-field mx-4">
                                    Change mode to:
                                </span>
								<v-select v-model="mode"
									:items="availableModes"
									class="modes-selector"
									variant="underlined"
									density="compact"
									hide-details
									:disabled="modeUpdating"
									@update:model-value="changeMode"/>
							</div>

							<div v-if="isStartLiveShow" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="iconStartLiveShow"/>
								<span class="event-field mx-4">
                                    Start Live Show
                                </span>
							</div>

							<div v-if="isEndLiveShow" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="iconEndLiveShow"/>
								<span class="event-field mx-4">
								End Live Show
							</span>
							</div>
						</div>

						<!-- Timers -->
						<div class="event-field ml-auto text-body-1" :class="{'text-h3': isNowPlaying}">
							<div class="d-flex align-center ml-3"
								v-if="(isPlayAsset && asset && hasAudio && !missingAudioPath) || isLiveRead || isSpotBlock || isLocalBreak">
								<div class="timer">
									<v-tooltip location="bottom" :text="durationTooltipText">
										<template v-slot:activator="{ props }">
											<div v-bind="props"
												:class="{'text-decoration-underline': isStretched || isSqueezed}">
												{{
												secondsToFormat(duration, "hh:mm:ss", {trim: "left", stopTrim: "m"})
												}}
											</div>
										</template>
									</v-tooltip>
								</div>
								<div class="timer">
									<v-tooltip v-if="hasAudio && !missingAudioPath" location="bottom" text="Intro">
										<template v-slot:activator="{ props }">
											<div v-bind="props" v-if="isNowPlaying">
												{{ secondsToFormat(remainingIntro, "mm:ss") }}
											</div>
											<div v-bind="props" v-else>
												{{ secondsToFormat(intro, "mm:ss") }}
											</div>
										</template>
									</v-tooltip>
								</div>
							</div>

							<div v-else-if="isMissingAudio || unresolvedTag" class="no-audio ml-3">
								MISSING AUDIO
							</div>
						</div>

						<div class="asset-actions d-flex justify-center">
							<!-- Active Future Event -->
							<div v-if="!isDisabledEvent && !hasError && !skippedEvent">
								<v-tooltip v-if="!isNowPlaying && !isLocalBreak && !isDVTBlockChild"
									location="bottom" text="Skip">
									<template v-slot:activator="{ props }">
										<v-btn v-bind="props" icon
											variant="text"
											density="compact"
											:disabled="skipSpotReasonMenu.open || isPlayed"
											@click.stop="skipEventClicked">
											<v-icon size="20" icon="mdi-eject" color="grey"/>
										</v-btn>
									</template>
								</v-tooltip>
							</div>

							<!-- Past Skipped Event -->
							<v-tooltip v-if="skippedEvent" location="bottom" text="Unskip">
								<template v-slot:activator="{ props }">
									<v-btn v-bind="props" icon
										variant="text"
										density="compact"
										:disabled="isDisabledEvent"
										@click.stop="skipEventClicked">
										<v-icon icon="mdi-eject" size="20" color="red"/>
									</v-btn>
								</template>
							</v-tooltip>

							<!-- Audio isPlayed -->
							<v-icon size="20" v-else-if="isPlayed" icon="mdi-check"/>

							<!-- Live Spots -->
							<div v-else-if="hasError && asset">
								<v-tooltip location="bottom" max-width="200" :text="errorMessage">
									<template v-slot:activator="{ props }">
										<v-icon v-bind="props" size="20" icon="mdi-alert"/>
									</template>
								</v-tooltip>
							</div>
						</div>
					</div>

					<v-row v-if="chainTypeIsStop" no-gutters class="stop-chain-type-warning-line justify-end">
						<div class="d-flex align-center" :class="darkMode ? 'text-secondary' : 'text-black'">
							<div class="text-caption">
								{{
								isFutureEvent || isNowPlaying ?
								`Audio will stop after this ${isSpotBlock ? 'spot block' : 'event'}.` :
								`Audio stopped after this ${isSpotBlock ? 'spot block' : 'event'}.`
								}}
							</div>
							<div class="px-4">
								<v-icon icon="mdi-stop-circle" size="16" color="#C6002B"/>
							</div>
						</div>
					</v-row>
				</v-card>

				<!-- Empty spot event placeholder -->
				<v-card v-else variant="outlined" rounded="0" width="100%">
					<v-container class="text-center">
						There are no Spots in this Spot Block
					</v-container>
				</v-card>

				<v-dialog width="500">
					<template v-slot:activator="{ props: activatorProps }">
						<div class="drop-target-indicator" :class="targetIndicatorClass"
							v-if="blinkingEvent || (targetIndicatorIsVisible && (isFutureEvent || isNowPlaying))">
							<v-btn v-bind="activatorProps" icon="mdi-plus-circle" size="24" variant="tonal"
								density="compact">
							</v-btn>
							<v-btn v-if="pasteIndicatorIsVisible"
								icon="mdi-content-paste" size="24" variant="tonal"
								density="compact" rounded="0" @click="pasteEvent">
							</v-btn>
						</div>
					</template>

					<template v-slot:default="{ isActive }">
						<InsertEventDialog
							:isActive="isActive.value"
							:selectedStation="selectedStation"
							:event="event"
							:playlistID="insideSpotBlock ? event.ChildPlaylistID : event.PlaylistID"
							:insideSpotBlock="insideSpotBlock"
							:show="parentComponent.currentOrSelectedShowEpisode?.show"
							@close="isActive.value = false"/>
					</template>
				</v-dialog>

			</v-hover>
		</v-expansion-panel-title>

		<v-expansion-panel-text v-if="isSpotBlock || isLocalBreak || isDVTBlock">
			<v-expansion-panels class="flex-column" variant="accordion" multiple>
				<draggable
					:list="childPlaylistEvents"
					:group="{ name: 'playlist', pull: 'clone', put: ['log', 'library-assets', 'playlist', 'hotkeys']}"
					filter=".not-draggable" :move="onMove" @end="onDropItem"
					:fallback-on-body="true"
					:force-fallback="true" :fallback-tolerance="3"
					:empty-insert-threshold="emptyInsertThreshold"
					:data-drop-container="isDVTBlock ? 'dvt' : 'playlist'"
					drag-class="dragged-playlist" :clone="handleDraggedClone">
					<PlaylistEvent v-for="item in childPlaylistEvents" :key="item.EventID"
						class="ml-1 drop-container"
						:class="{spot: isSpotBlock, 'not-draggable': !parentComponent.isDraggableEvent(event) || !dragSpotFeatureEnabled}"
						:selectedStation="selectedStation"
						:event="item"
						:id="item.EventID"
						:data-station-id="item.StationID"
						:data-playlist-id="item.PlaylistID"
						:data-child-playlist-id="item?.ChildPlaylistID"
						:isDVTBlockChild="isDVTBlock"
						:draggedEvent="draggedEvent"
						:dropTargetEvent="parentComponent.loadDropTargetEvent(item.EventID)"
						:parentComponent="parentComponent"
						:copiedEvent="copiedEvent"
						@copy-event="parentComponent.copyEvent(item)"
						@paste-event="parentComponent.pasteEvent"
						:type="blockType"
						:data-drop-container="isDVTBlock ? 'dvt' : 'playlist'"
					/>
					<PlaylistEvent v-if="!childPlaylistEvents.length"
						class="ml-1 empty-spot-placeholder not-draggable"
						:id="`${event.ChildPlaylistID}-00000000-0000-0000-0000-000000000000`"
						:selectedStation="selectedStation"
						:event="emptyBlockContainerEvent"
						:data-station-id="event.StationID"
						:data-playlist-id="event.ChildPlaylistID"
						:data-child-playlist-id="event?.ChildPlaylistID"
						:dropTargetEvent="parentComponent.loadDropTargetEvent(event.EventID)"
						:parentComponent="parentComponent"
						data-drop-container="playlist"
					/>
				</draggable>
			</v-expansion-panels>
		</v-expansion-panel-text>

		<v-menu v-model="contextMenu.open" content-class="elevation-24" :target="[contextMenu.x, contextMenu.y]">
			<template v-slot:activator="{props}">
				<div v-bind="props"/>
			</template>
			<v-list>
				<v-tooltip location="bottom" :disabled="!!asset" text="Missing Audio">
					<template v-slot:activator="{ props }">
						<div v-bind="props">
							<v-list-item v-if="isPlayAsset"
								:disabled="!asset || auditionAudioMenu.open"
								@click="openAuditionAudioWidget($event, asset)">
								<v-list-item-title class="text-body-2">
									Audition Audio
								</v-list-item-title>
							</v-list-item>
						</div>
					</template>
				</v-tooltip>
				<v-menu v-if="isETM" content-class="elevation-1 confirmation-card" location="end"
					:close-on-content-click="false">
					<template v-slot:activator="{ props }">
						<v-list-item v-bind="props">
							<v-list-item-title class="text-body-2">
								Edit Event
							</v-list-item-title>
						</v-list-item>
					</template>
					<ETMConfirmation :current-type="event.EtmType"
						:hard-time="event.HardTime"
						:button-options="buttonOptions"
						@agree="(duration, type) => formatDurationAndUpdateETMType(duration, type)"
						@cancel="() => contextMenu.open = false"
					/>
				</v-menu>
				<v-list-item v-if="!expiredKillDateTime" @click.stop="copyEvent" :disabled="!asset">
					<v-list-item-title class="text-body-2">
						Copy Event
					</v-list-item-title>
				</v-list-item>
				<v-list-item v-if="!expiredKillDateTime" @click.stop="pasteEvent"
					:disabled="!copiedEvent || (!isFutureEvent && !asset) || isPlayed || isRemainingTimeRestricted">
					<v-list-item-title class="text-body-2">
						Paste Event
					</v-list-item-title>
				</v-list-item>
				<v-menu v-if="!expiredKillDateTime" content-class="elevation-1 confirmation-card" location="end"
					:close-on-content-click="false">
					<template v-slot:activator="{ props }">
						<v-list-item v-bind="props" v-if="isSpotBlock">
							<v-list-item-title class="text-body-2">
								Edit Length
							</v-list-item-title>
						</v-list-item>
					</template>
					<SpotBlockConfirmation
						@agree="(d) => editTargetLength(d)"
						@cancel="() => contextMenu.open = false"
					/>
				</v-menu>
				<v-list-item v-if="!isNowPlaying && !isLocalBreak && !isDVTBlockChild"
					:disabled="!isFutureEvent || skipSpotReasonMenu.open || (skippedEvent && isDisabledEvent)"
					@click.stop="skipEventClicked">
					<v-list-item-title class="text-body-2">
						{{ `${skippedEvent ? "Unskip" : "Skip"} Event` }}
					</v-list-item-title>
				</v-list-item>
				<v-list-item v-if="!expiredKillDateTime" @click.stop="resolveTag" :disabled="!event.ResolveTags">
					<v-list-item-title class="text-body-2">
						Resolve Tag
					</v-list-item-title>
				</v-list-item>
				<v-menu v-if="!expiredKillDateTime" content-class="elevation-1 confirmation-card" location="end"
					:disabled="(!asset && !isSpotBlock) || isSyncDisabled"
					:close-on-content-click="false">
					<template v-slot:activator="{ props }">
						<v-list-item v-bind="props" :disabled="(!asset && !isSpotBlock) || isSyncDisabled"
							v-if="!isFutureSyndicatedShowEvent">
							<v-list-item-title class="text-body-2">
								Sync to selection
							</v-list-item-title>
						</v-list-item>
					</template>
					<SyncToSelectionConfirmation :message="syncMessage"
						:showEpisode="isSyndicatedShowEvent ? event : null"
						:selectedStation="selectedStation"
						@agree="(...args) => syncToSelection(...args)"
						@cancel="contextMenu.open = false"/>
				</v-menu>
				<v-list-item v-if="canBeCuedAsNext"
					:disabled="cueingAsNext"
					@click.stop="cueAsNext">
					<v-list-item-title class="text-body-2">
						Cue as next
					</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>

		<SkipFloatMenu v-if="skipSpotReasonMenu.open"
			:floatMenuModel="skipSpotReasonMenu"
			:floatMenuForm="skipSpotForm"
			:listTypes="skipTypes"
			@submitEvent="skipEvent"
			@close-menu="closeSkipSpotReasonMenu"/>

		<AuditionAudioWidget v-if="auditionAudioMenu.open && isPlayAsset && asset"
			:menu="auditionAudioMenu"
			:event="event"
			parentComponent="playlist"
			ref="auditionAudioWidget"
			@close-menu="closeAuditionAudioWidget"/>

		<v-dialog v-model="targetLengthOverlay" width="auto">
			<v-card color="white">
				<v-card-text>
					You've set the Spot Block Target Length greater than 10 minutes! <br/>
					Are you sure you want to set the Spot Block Target Length to <b> {{
					                                                                 secondsToFormat(
					                                                                 targetLengthDuration,
					                                                                 "mm:ss"
					                                                                 )
					                                                                 }} </b>?
				</v-card-text>
				<v-card-actions>
					<v-btn color="primary" @click="updateSpotBlockTargetLength">
						Proceed
					</v-btn>
					<v-btn @click="targetLengthOverlay = false">
						Cancel
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog :model-value="skipSpotBlockConfirmationDialog.open" width="500" persistent>
			<GeneralConfirmation
				:type="ConfirmationType.General" :message="skipSpotBlockConfirmationDialog.message"
				@agree="d => skipEvent({skip:true}, d)"
				@cancel="() => {contextMenu.open = false; skipSpotBlockConfirmationDialog.open = false;}"/>
		</v-dialog>
	</v-expansion-panel>
</template>

<script>
/* Libraries */
import * as _ from "lodash";
import moment from "moment-timezone";
import {VueDraggableNext} from "vue-draggable-next";
import emitter from "@/emitter";

/* Modules */
import AuditionAudioWidget from "@/components/AuditionAudioWidget.vue";
import Image from "@/components/Image.vue";
import InsertEventDialog from "@/components/dialogs/InsertEventDialog.vue";
import SkipFloatMenu from "@/components/SkipFloatMenu.vue";
import SyncToSelectionConfirmation from "@/components/dialogs/SyncToSelectionConfirmation.vue";
import SpotBlockConfirmation from "@/components/dialogs/SpotBlockConfirmation.vue";
import ETMConfirmation from "@/components/dialogs/ETMConfirmation.vue";
import GeneralConfirmation from "@/components/dialogs/GeneralConfirmation.vue";

/* Enums */
import ConfirmationType from "@/enums/ConfirmationType";
import EditCodes from "@/enums/EditCodes";
import ErrorCode from "@/enums/ErrorCode";
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import POITypes from "@/enums/POITypes";
import ResyncType from "@/enums/ResyncType";
import SequencerModes from "@/enums/SequencerModes";
import SequencerStatus from "@/enums/SequencerStatus";
import BedTypes from "@/enums/BedTypes";
import EtmTypes from "@/enums/EtmTypes";

/* Mixins */
import AssetPropertiesMixin from "@/mixins/AssetProperties";
import AuditionAudioMixin from "@/mixins/AuditionAudio";
import DragAndDropMixin from "@/mixins/DragAndDrop";
import DurationFormatMixin from "@/mixins/DurationFormatMixin";
import EventProperties from "@/mixins/EventProperties";

export default {
	name: "PlaylistEvent",

	components: {
		GeneralConfirmation,
		ETMConfirmation,
		SpotBlockConfirmation,
		SyncToSelectionConfirmation,
		AuditionAudioWidget,
		draggable: VueDraggableNext,
		Image,
		InsertEventDialog,
		SkipFloatMenu
	},

	mixins: [
		AssetPropertiesMixin,
		AuditionAudioMixin,
		DragAndDropMixin,
		DurationFormatMixin,
		EventProperties
	],

	props: [
		"selectedStation",
		"event",
		"type",
		"draggedEvent",
		"dropTargetEvent",
		"parentComponent",
		"copiedEvent",
		"isDVTBlockChild"
	],

	emits: ["copyEvent", "pasteEvent"],

	data() {
		return {
			targetLengthOverlay: false,
			targetLengthDuration: null,
			targetIndicatorIsVisible: false,
			insertEventDialog: false,
			hoverToPaste: false,
			cueingAsNext: false,
			buttonOptions: {
				agreeBtn: {
					enabled: true,
					message: "Update"
				},
				cancelBtn: {
					enabled: true,
					message: "Cancel"
				}
			}
		}
	},

	computed: {
		EtmTypes() {
			return EtmTypes
		},

		ConfirmationType() {
			return ConfirmationType;
		},

		asset() {
			return this.event.Asset;
		},

		timezone() {
			return this.selectedStation.StationTimeZone;
		},

		isStretched() {
			const event = this.sequencer?.Items.find(i => i.EventID === this.event.EventID);
			return event?.PitchlessPlayRate && event?.PitchlessPlayRate > 0;
		},

		stretchedValue() {
			const event = this.sequencer?.Items.find(i => i.EventID === this.event.EventID);
			return event?.PitchlessPlayRate;
		},

		isSqueezed() {
			const event = this.sequencer?.Items.find(i => i.EventID === this.event.EventID);
			return event?.PitchedPlayRate && event?.PitchedPlayRate > 0;
		},

		squeezedValue() {
			const event = this.sequencer?.Items.find(i => i.EventID === this.event.EventID);
			return event?.PitchedPlayRate;
		},

		durationTooltipText() {
			if (this.isSqueezed) {
				return `Duration - Squeezed by: ${parseFloat(((this.squeezedValue - 1) * 100).toFixed(2))}%`
			}
			else if (this.isStretched) {
				return `Duration - Stretched by: ${parseFloat(((this.stretchedValue - 1) * 100).toFixed(2))}%`
			}
			else {
				return "Duration";
			}
		},

		spotBlockContainsLiveRead() {
			return this.isSpotBlock && this.childPlaylistEvents.filter(e => e.LinearAdData?.liveRead).length > 0;
		},

		hasError() {
			let error;
			if (this.isPlayAsset) {
				error = [EventStatus.NotPlayed, EventStatus.PendingNotPlayed].includes(this.statusCode) ||
				        this.expiredKillDateTime || this.event.ErrorCode > 0;
			}
			else if (this.isSpotBlock || this.isLocalBreak) {
				error = this.childPlaylistEvents.some((e) => {
					return [EventStatus.NotPlayed, EventStatus.PendingNotPlayed, EventStatus.PendingNotPlayed]
						.includes(e.StatusCode);
				});
			}
			return error;
		},

		expiredKillDateTime() {
			return moment().isAfter(this.asset?.KillDateTime);
		},

		errorMessage() {
			let error;
			if (this.isPlayAsset) {
				error = this.expiredKillDateTime ? "This asset is expired" :
				        ErrorCode.getMessageByValue(this.event.ErrorCode);
			}
			else if (this.isSpotBlock || this.isLocalBreak) {
				error = "There are events with error inside this block.";
			}
			return error;
		},

		displayAirTime() {
			return this.asset ||
			       this.isETM ||
			       this.isRejoin ||
			       this.isSpotBlock ||
			       this.isLocalBreak ||
			       this.isModeSelectionEvent ||
			       this.isComment ||
			       this.isStartLiveShow ||
			       this.isEndLiveShow;
		},

		airTime() {
			let airtime = this.event.AirStartDateTime && this.statusCode !== EventStatus.NotPlayed ?
			              moment.tz(
							  this.event.AirStartDateTime,
							  this.timezone
						  ).format("HH:mm:ss") :
			              null;

			// In Idle state (playlist has stopped), clear the airtimes for the future events of the current and
			// next hour playlists
			if (this.$store.getters.getSequencerStatus(this.selectedStation.StationID) === SequencerStatus.Idle.val &&
			    this.isFutureEvent) {
				const playlist = this.$store.getters.getPlaylistById(
					{stationID: this.selectedStation.StationID, playlistID: this.event.PlaylistID}
				);
				if (moment(playlist.ScheduledStartDateTime).isSameOrBefore(moment().add(1, "hour"))) {
					airtime = null;
				}
			}
			return airtime;
		},

		editETMFeatureEnabled() {
			return this.$store.getters.isFeatureEnabled({
				object_id: this.selectedStation.StationID,
				reference: "zettacloud_event_edit_non_asset"
			});
		},

		description() {
			return this.event.Description;
		},

		gapTime() {
			return this.$store.getters.getSequencerItemByEventID(
				this.selectedStation.StationID,
				this.event.EventID
			)?.GapTime ?? this.event.GapTime;
		},

		duration() {
			if (this.isPlayAsset) {
				return this.sequencerEvent?.DurationToSegue ?? this.asset?.POIMarkup?.Length ?? 0;
			}
			else if (this.isLiveRead) {
				return this.event.LinearAdData?.spotLength ?? 0;
			}
			else if (this.isComment) {
				return 0;
			}
			else { //SpotBlock or LocalBreak or DVTBlock
				if (this.childPlaylistEvents?.some(e => e.Asset?.BedType === BedTypes.Bed)) {
					// Split events into Ready and Invalid
					const [bedReadyEvents, bedInvalidEvents] = _.partition(
						this.childPlaylistEvents,
						e => e.Asset?.BedType === BedTypes.Bed
					);

					// Calculate Ready Duration
					const bedReadyDuration = bedReadyEvents.reduce((total, e) => {
						const asset = e.Asset;
						const poiMarkupOverride = this.$store.getters.getAssetPOIMarkupOverride(asset?.AssetID);
						const markers = (poiMarkupOverride ?? asset?.POIMarkup)?.POIMarkers || [];

						// Prioritize EarlySegue marker, fallback to PlaybackView, then Length
						const earlySegueMarker = markers.find(p => p.POIType === POITypes.EarlySegue);
						const duration = earlySegueMarker?.Position ?? 0;

						return total + duration;
					}, 0);

					// Calculate Invalid Duration and combine
					const bedInvalidDuration = this.playlistSpotBlockDuration(bedInvalidEvents);
					return bedReadyDuration + bedInvalidDuration;
				}
				else {
					return this.sequencerEvent ?
					       this.sequencerSpotBlockDuration(this.sequencerEvent) :
					       this.playlistSpotBlockDuration(this.event.ChildPlaylist?.Events ?? []);
				}
			}
		},

		remainingIntro() {
			return this.$store.getters.getSequencerItemByEventID(
				this.selectedStation.StationID,
				this.event.EventID
			)?.RemainingIntro;
		},

		blockType() {
			if (this.isSpotBlock) {
				return "spotBlockEvent";
			}
			else if (this.isDVTBlock) {
				return "DVTBlockEvent";
			}
			else {
				return "localBreakEvent";
			}
		},

		isDisabledEvent() {
			let disabled = false;

			if (this.type === "DVTBlockEvent") {
				return false;
			}

			// Check StatusCode
			if (![EventStatus.Ready, EventStatus.Current, EventStatus.Paused].includes(this.event.StatusCode)) {
				disabled = true;
			}

			// Check ResyncType
			if (this.resyncType === ResyncType.Full) {
				disabled = true;
			}

			// Check playlist's HotlineSequence for not now playing events
			if (!this.isNowPlaying) {
				const playlist = this.$store.getters.getPlaylistById(
					{stationID: this.selectedStation.StationID, playlistID: this.event.PlaylistID}
				);
				if (this.event.SequenceNumber < playlist?.HotlineSequence) {
					disabled = true;
				}
			}

			return disabled;
		},

		skippedEvent() {
			return this.event.Skipped;
		},

		potNumber() {
			// Get the pot number from the Sequencer for the current and future events or from the event properties for the played ones
			return this.sequencerEvent?.PotNumber ?? this.event.PotNumber;
		},

		childPlaylistEvents() {
			return _.sortBy(
				this.event.ChildPlaylist?.Events?.filter(
					e => !e.Skipped &&
					     [
							 EventTypes.PlayAsset,
						     EventTypes.SpotBlock,
						     EventTypes.DVTBlock,
						     EventTypes.Comment
						 ].includes(e.EventType)
				),
				"SequenceNumber"
			) ?? [];
		},

		blinkingEvent() {
			return this.$store.getters.getBlinkingEvent(this.event.EventID);
		},

		pasteIndicatorIsVisible() {
			const isDisabled = this.event.empty ? this.event.isDisabled : this.isDisabledEvent;
			const isFuture = this.event.empty ? this.event.isFuture : this.isFutureEvent;

			return !isDisabled && (isFuture || (this.isNowPlaying && !this.isRemainingTimeRestricted)) &&
			       this.copiedEvent &&
			       (
					   (this.hoverToPaste && !this.copiedEvent.pasteBeforeEvent) ||
				       this.copiedEvent.pasteBeforeEvent === this.event.EventID
				   );
		},

		targetIndicatorClass() {
			return this.blinkingEvent ? "blink" :
			       this.dropTargetEvent ?
			       `insert-${this.dropTargetEvent.position} ${this.dropTargetEvent.blink ? "blink" : ""}` :
			       this.copiedEvent ? `insert-before ${this.copiedEvent.blink ? "blink" : ""}` : "";
		},

		unrecordedVoiceTrack() {
			return this.isVoiceTrack && !this.asset;
		},

		missingAudioPath() {
			return this.isPlayAsset && !this.event?.Asset?.POIMarkup?.Path;
		},

		isMissingAudio() {
			return this.isPlayAsset && this.asset && this.missingAudioPath;
		},

		isRemainingTimeRestricted() {
			return this.$store.getters.getSequencerItemByEventID(
				this.selectedStation.StationID,
				this.parentComponent.nowPlayingEvent?.EventID
			)?.RemainingTime < 5;
		},

		isDroppedByETM() {
			return this.resyncType === ResyncType.Full;
		},

		isPartiallyDroppedByETM() {
			return this.event.ErrorCode === ErrorCode.EtmDropped || this.resyncType === ResyncType.Partial;
		},

		unresolvedTag() {
			if (!this.asset && this.event.ResolveTags) {
				return this.event.ResolveTags.join();
			}
			return false;
		},

		resyncType() {
			return this.sequencerEvent?.ResyncType ?? this.event.ResyncType;
		},

		syncMessage() {
			const dateNow = moment.tz(this.event.AirStartDateTime, this.timezone)
			                      .format("YYYY-MM-DD HH:mm:ss");
			return `Are you sure you want to sync the Now Playing playback to \n <b>${dateNow}</b>?`;
		},

		isSyndicatedShowEvent() {
			return this.isValidURL(this.asset?.POIMarkup?.Path);
		},

		isFutureSyndicatedShowEvent() {
			if (this.isSyndicatedShowEvent) {
				return moment().isBefore(this.asset.ReleaseDateTime);
			}
			return false;
		},

		isSyncDisabled() {
			const playlist = this.$store.getters.getPlaylistById(
				{stationID: this.selectedStation.StationID, playlistID: this.event.PlaylistID}
			);
			return moment().isBefore(playlist.ScheduledStartDateTime);
		},

		emptyBlockContainerEvent() {
			return {
				empty: true,
				EventID: `${this.event.ChildPlaylistID}-00000000-0000-0000-0000-000000000000`,
				isDisabled: this.isDisabledEvent,
				isFuture: this.isFutureEvent
			};
		},

		clientId() {
			return this.$store.getters.clientId();
		},

		availableModes() {
			return this.selectedStation?.AvailableModes?.map(mode => {
				return {
					value: mode,
					title: SequencerModes.getName(mode)
				}
			}) ?? [];
		},

		lgAndDown() {
			return this.$vuetify.display.lgAndDown;
		},

		canBeCuedAsNext() {
			return (!!this.asset || !!this.isSpotBlock) && this.isFutureEvent && !this.isNextEvent &&
			       !this.isRemainingTimeRestricted && !this.isDisabledEvent && !this.skippedEvent &&
			       !this.expiredKillDateTime && !this.$el.classList.contains("spot");
		},

		insideSpotBlock() {
			return this.isSpotBlock && this.$el.classList.contains("v-expansion-panel--active")
		},

		dragSpotFeatureEnabled() {
			return this.$store.getters.isFeatureEnabled({
				object_id: this.selectedStation.StationID,
				reference: "zettacloud_event_move_spot"
			});
		},

		auditionAudioIsOpen() {
			return this.$store.getters.auditionAudioIsOpen(this.event.EventID);
		}
	},

	methods: {
		getColors() {
			let eventTypeColor;

			if (this.isPlayAsset) {
				if (!this.isMissingAudio && !this.unresolvedTag) {
					if (!this.unrecordedVoiceTrack) {
						if (this.asset?.POIMarkup?.Length && this.assetType?.Color != null) {
							eventTypeColor = this.assetType?.Color;
						}
						else {
							eventTypeColor = "rgb(255,0,0)";
						}
					}
					else {
						eventTypeColor = "rgb(0,0,0)";
					}
				}
				else {
					eventTypeColor = "rgb(45,45,45)";
				}
			}
			else if (this.isSpotBlock) {
				eventTypeColor = this.spotBlockColor;
			}
			else if (this.isLocalBreak) {
				eventTypeColor = this.localBreakColor;
			}
			else if (this.isETM || this.isRejoin) {
				eventTypeColor = this.colorETM;
			}
			else if (this.isLiveRead) {
				eventTypeColor = this.liveReadColor;
			}
			else if (this.isComment) {
				eventTypeColor = this.commentColor;
			}
			else if (this.isModeSelectionEvent) {
				eventTypeColor = this.modeSelectionEventColor;
			}
			else if (this.isDVTBlock) {
				eventTypeColor = this.DVTBlockEventColor;
			}

			return eventTypeColor;
		},

		setColors() {
			if (!this.isDroppedByETM) {
				let eventTypeColor = this.getColors();

				if (eventTypeColor) {
					const rootCard = this.$refs.rootCard?.$el || this.$refs.rootCard;
					rootCard?.style
					        .setProperty("--eventTypeColorRGBValues", eventTypeColor.match(/\d+/g).join(",")); // Get only the rgb values
				}
			}
		},

		sequencerSpotBlockDuration(sequencerEvent) {
			const childItems = sequencerEvent.ChildItems?.filter(
				e => !e.Skipped && [
					EventTypes.PlayAsset,
					EventTypes.SpotBlock,
					EventTypes.DVTBlock,
					EventTypes.Comment
				].includes(e.EventType)
			) ?? [];

			return childItems.reduce((acc, e) => acc + (e.DurationToSegue ?? 0), 0);
		},

		playlistSpotBlockDuration(childPlaylistEvents) {
			const events = childPlaylistEvents.filter(
				e => !e.Skipped && [
					EventTypes.PlayAsset,
					EventTypes.SpotBlock,
					EventTypes.DVTBlock,
					EventTypes.Comment
				].includes(e.EventType)
			);

			return events.reduce((acc, e) => acc + (e.Asset?.POIMarkup?.Length ?? 0), 0);
		},

		syncToSelection({switchToAutoMode = true, syncInput = {}} = {}) {
			let msg = "";
			if (!_.isNil(switchToAutoMode)) {
				this.$store.commit(
					"setSwitchToAutoMode",
					{stationID: this.selectedStation.StationID, switchToAutoMode}
				);
			}

			if (this.isSyndicatedShowEvent) {
				const playlist = this.$store.getters.getPlaylistById(
					{stationID: this.selectedStation.StationID, playlistID: this.event.PlaylistID}
				);
				const duration = moment(playlist.ScheduledEndDateTime).diff(playlist.ScheduledStartDateTime, "seconds");

				const startLiveShowPOI = this.event.OverridePOIMarkup.POIMarkers
				                             .find(m => m.POIType === POITypes.StartLiveShow)?.Position ??
				                         this.asset.POIMarkup.POIMarkers
				                             .find(m => m.POIType === POITypes.StartLiveShow)?.Position ?? 0;
				const fadeOutPOI = this.event.OverridePOIMarkup.POIMarkers
				                       .find(m => m.POIType === POITypes.FadeOut)?.Position ?? duration;

				const OriginalOverridePOIStart = Math.min(fadeOutPOI - duration, 0);

				let syncPosition = moment.duration({
					hours: syncInput.hours,
					minutes: syncInput.minutes,
					seconds: syncInput.seconds
				}).asSeconds();

				// We need to make sure that the position is not in the future of a live asset and
				// also not very close to live
				const assetReleaseDateTime = moment(this.event.Asset.ReleaseDateTime);
				if (assetReleaseDateTime.clone().add(syncPosition, "seconds")
				                        .isSameOrAfter(moment().add(6, "seconds"))
				) {
					syncPosition = moment().diff(assetReleaseDateTime, "seconds") - 6;
				}

				if (syncPosition < 0) {
					syncPosition = 0;
				}

				msg = {
					RestartPosition: OriginalOverridePOIStart + syncPosition + startLiveShowPOI
				};
				console.log("RestartPosition: ", moment.duration(msg.RestartPosition, "seconds").format("hh:mm:ss"));
			}

			this.$store.dispatch("sequencerSyncActionRequest", {
				action: `SyncToEvent.${this.event.EventID}`,
				msg,
				stationID: this.selectedStation.StationID
			});
			this.contextMenu.open = false;
		},

		formatDurationAndUpdateETMType(duration, etmType) {
			const mins = duration?.minutes;
			const secs = duration?.seconds;

			// If both are missing, hardTime is null. Otherwise, calculate the total seconds.
			const hardTime = (_.isNil(mins) && _.isNil(secs)) ?
			                 null :
			                 (mins || 0) * 60 + (secs || 0);

			this.updateETMType(hardTime, etmType);
		},

		updateETMType(duration, etmType) {
			this.contextMenu.open = false;

			this.$store.dispatch("updatePlaylistEvent", {
				EventID: this.event.EventID,
				EtmType: etmType,
				HardTime: parseInt(duration)
			});

		},

		copyEvent() {
			this.$emit("copyEvent");
			this.contextMenu.open = false;

			if (this.$refs.auditionAudioWidget?.waveform) {
				this.$refs.auditionAudioWidget.stopAudioWaveform();
			}
		},

		pasteEvent() {
			if (!this.isRemainingTimeRestricted) {
				this.$emit("pasteEvent", this.event, this.$el);
			}
			else {
				this.$store.commit(
					"SHOW_NOTIFICATION_WITH_TYPE",
					{message: "You cannot paste an event on a past airtime."}
				);
			}
			this.contextMenu.open = false;

			if (this.$refs.auditionAudioWidget?.waveform) {
				this.$refs.auditionAudioWidget.stopAudioWaveform();
			}
		},

		editTargetLength(targetLength) {
			this.contextMenu.open = false;

			this.targetLengthDuration = moment.duration({
				minutes: targetLength.minutes,
				seconds: targetLength.seconds
			}).asSeconds();

			if (this.targetLengthDuration > 600) {
				this.targetLengthOverlay = true;
			}
			else {
				this.updateSpotBlockTargetLength();
			}
		},

		updateSpotBlockTargetLength() {
			if (this.targetLengthOverlay) {
				this.targetLengthOverlay = false;
			}

			this.$store.dispatch("updatePlaylist", {
				playlistID: this.event.ChildPlaylistID,
				TargetLength: this.targetLengthDuration
			});
		},

		resolveTag() {
			this.$store.dispatch("getDefaultAssetTypes").then((response) => {
				let assetTypes = "";
				if (response.status === 200 && response.data.LocalizerAssetTypeIDs) {
					assetTypes = response.data.LocalizerAssetTypeIDs.map(assetType => `"${assetType}"`);
				}
				this.$store.dispatch("resolveTag", {
					stationID: this.selectedStation.StationID,
					eventID: this.event.EventID,
					playlistID: this.event.PlaylistID,
					searchShowDateTime: moment.tz(this.event.AirStartDateTime, this.timezone).toISOString(true),
					assetTypes: assetTypes
				}).then((response) => {
					if (response.status === 200) {
						if (response.data.errors) {
							this.$store.commit(
								"SHOW_NOTIFICATION_WITH_TYPE",
								{message: response.data.errors[0].message}
							);
						}
						else {
							this.$store.commit(
								"SHOW_SUCCESS_NOTIFICATION",
								{message: response.data.data.resolveVoiceTagEvent.resolveReason}
							);
						}
					}
					else {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {message: "Could not resolve rotation tag!"});
					}
				})

			})
			this.contextMenu.open = false;
		},

		isValidURL(url) {
			try {
				return Boolean(new URL(url));
			} catch (e) {
				return false;
			}
		},

		skipEventClicked($event) {
			if (!this.skippedEvent) {
				if (this.assetType?.Description === "Spot") {
					this.openSkipSpotReasonMenu($event);
				}
				else {
					if (this.isSpotBlock) {
						this.skipSpotBlockConfirmationDialog.open = true;
					}
					else {
						this.skipEvent({skip: true});
					}
				}
			}
			else {
				this.skipEvent({skip: false});
			}
		},

		changeMode(mode) {
			this.modeUpdating = true;
			this.$store.dispatch("updatePlaylistEvent", {
				EventID: this.event.EventID,
				ExecuteCommand: {
					ExecuteCommandType: 4,
					ModeChange: {
						NewMode: mode
					}
				}
			});
		},

		onMove(evt, originalEvent) {
			const draggedElement = document.querySelector(".dragged-playlist");
			this.onDragItem(evt, originalEvent, draggedElement);
			return false;
		},

		async cueAsNext() {
			const nowPlayingEvent = this.parentComponent.nowPlayingEvent;
			this.cueingAsNext = true;

			const newSequenceNumber = await this.$store.dispatch("generateSequenceNumber", {
				eventID: nowPlayingEvent.EventID,
				afterEvent: true
			}).then(response => {
				return response.data[0];
			}).catch(error => {
				console.log(error);
				this.$store.commit(
					"SHOW_NOTIFICATION_WITH_TYPE",
					{message: "An error occurred when trying to generate the event's sequence number."}
				);
				this.contextMenu.open = false;
				this.cueingAsNext = false;

				return null;
			});

			if (newSequenceNumber != null) {
				this.$store.dispatch("updatePlaylistEvent", {
					PlaylistID: nowPlayingEvent.PlaylistID,
					EventID: this.event.EventID,
					SequenceNumber: newSequenceNumber,
					StatusCode: EventStatus.Ready,
					EditCode: EditCodes.ManualReorder,
					EditComment: this.$store.getters.getUserFullName()
				}).then(() => {
					this.$store.commit(
						"SHOW_SUCCESS_NOTIFICATION",
						{message: "Event successfully cued as next!"}
					);
				}).catch(error => {
					console.error(error);
					this.$store.commit(
						"SHOW_NOTIFICATION_WITH_TYPE",
						{message: "An error occurred when trying to cue the event as next."}
					);
				}).finally(() => {
					this.contextMenu.open = false;
					this.cueingAsNext = false;
				});
			}
		},

		copyToClipboard(textToCopy) {
			if (typeof textToCopy === "object") {
				textToCopy = JSON.stringify(textToCopy);
			}
			navigator.clipboard.writeText(textToCopy)
			         .then(() => {
						 this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
							 message: "Copied successfully!"
						 });
					 })
		}
	},

	watch: {
		asset() {
			this.$nextTick(() => {
				this.setColors();
			});
		},

		isDroppedByETM(dropped, previousDropped) {
			if (!dropped && previousDropped) {
				this.$nextTick(() => {
					this.setColors();
				});
			}
		},

		"contextMenu.open"(open) {
			const rootCard = this.$refs.rootCard?.$el || this.$refs.rootCard;
			rootCard?.classList.toggle("elevation-4", open);
			this.$el.classList?.toggle?.("context-menu-open", open);
			emitter.emit("toggle-playlist-context-menu");
		},

		auditionAudioIsOpen(open) {
			if (open) {
				this.openAuditionAudioWidget(null, this.asset);
			}
			else {
				this.closeAuditionAudioWidget();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.v-card:not(.skip-spot-block-reason-container):not(.confirmation-card) {
	background: rgba(var(--eventTypeColorRGBValues), .07);
	border: none;
	margin-bottom: 1px;
	-webkit-user-select: none; /* Safari */
	-ms-user-select: none; /* IE 10 and IE 11 */
	user-select: none; /* Standard syntax */

	.asset-container {
		border-width: 0 0 0 8px;
		border-color: rgba(var(--eventTypeColorRGBValues), 1);
		border-style: solid;
		height: 50px;
		padding: 0 0 0 8px;
		position: relative;

		.pot-number {
			background: #ffffff;
			border: 1px solid rgb(var(--eventTypeColorRGBValues));
			border-radius: 2px;
			color: $grey-400;
			font-size: 24px;
			font-weight: 700;
			line-height: 36px;
			min-width: 30px;
			padding: 1px 5px;
			text-align: center;

			&.empty {
				background: transparent;
				border-color: transparent !important;
			}

			@media only screen and (max-width: 1920px) {
				padding: 1px 2px;
			}
		}

		.airtime {
			min-width: 110px;
		}

		.now-playing-label {
			color: rgb(var(--eventTypeColorRGBValues));
			font-size: 12px;
			font-weight: 700;
			display: inline-flex;
			align-items: center;
			position: relative;
			bottom: 3px;

			> .v-icon {
				color: rgb(var(--eventTypeColorRGBValues));
			}
		}

		.chain-type {
			min-width: 60px;
		}

		.asset-type-icon {
			color: rgba(var(--eventTypeColorRGBValues), 1);
			margin-right: 8px;
			min-width: 35px;
		}

		.timer {
			min-width: 60px;
		}

		.no-audio {
			color: white;
			min-width: 120px;
		}

		.asset-actions {
			min-width: 60px;

			.v-btn {
				min-width: 30px;
			}
		}
	}

	&.disabled-event {
		background: rgba(113, 114, 119, .12);

		.asset-container {
			border-color: rgba(var(--eventTypeColorRGBValues), 1);

			.pot-number:not(.empty) {
				border-color: $grey-400;
				color: $grey-300;
			}

			.event-field,
			.event-field .v-icon,
			.asset-actions * {
				color: $grey-400;
			}
		}

		:deep(.v-field__input) {
			cursor: default;
		}
	}


	&.skipped-event {
		background: $ihm-grey1;

		.asset-container {
			border-color: rgba(var(--eventTypeColorRGBValues), 1);

			.pot-number:not(.empty) {
				border-color: $grey-400;
				color: $grey-300;
			}

			.event-field,
			.event-field .v-icon,
			.asset-actions * {
				color: $grey-300;
			}
		}

		:deep(.v-field__input) {
			cursor: default;
		}
	}

	&.missing-audio {
		background: #484C4F !important;
		outline: 1px dashed #2D2D2D;

		.v-icon:not(.mdi-alert, .mdi-eject, .mdi-information-outline, .asset-type-icon, .mdi-cancel) {
			color: #E22100 !important;
			background: radial-gradient(white 50%, transparent 50%);
		}

		.v-icon:not(:not(.mdi-information-outline, .asset-type-icon, .mdi-cancel, .mdi-alert)) {
			color: white !important;
		}

		:hover {
			.v-icon:not(.mdi-close-circle,.mdi-play,.asset-type-icon) {
				color: white !important;
			}
		}
	}

	&.unrecorded-vt {
		background: rgba(255, 218, 35, 0.55) !important;
		outline: 1px dashed #C19703;
	}

	.stop-chain-type-warning-line {
		height: 24px;
		background: $ihm-grey1;

		div {
			letter-spacing: .5px !important;
		}
	}

	.gap-time {
		color: $ihm-orange1;
		font-weight: bold;
	}

	.live-read {
		background: $ihm-white;
		color: $ihm-red2;
	}

	&.v-theme--dark {
		background-color: transparent;

		.asset-container {
			color: rgba(var(--eventTypeColorRGBValues), 1);

			.v-image {
				filter: invert(100%) sepia(100%) saturate(0%) brightness(300%) !important;
			}

			.v-icon.mdi-information-outline {
				color: rgba(var(--eventTypeColorRGBValues), 1);
			}

			.pot-number {
				background-color: transparent;
				border-color: rgba(var(--eventTypeColorRGBValues), 1);
				color: $grey-300;
			}

			&.now-playing {
				background-color: rgba(var(--eventTypeColorRGBValues), 0.61);
				border-width: 1px 1px 1px 8px;
				color: $ihm-white;

				.now-playing-label {
					color: $ihm-white;

					> .v-icon {
						color: $ihm-white;
					}
				}

				.pot-number,
				.v-icon.mdi-information-outline {
					color: $ihm-white;
				}
			}
		}

		&.disabled-event {
			background-color: rgba(55, 57, 61, 1);

			.asset-container {
				.event-field,
				.event-field .v-icon,
				.asset-actions * {
					color: $grey-300;
				}

				.v-image {
					filter: unset !important;
				}

				.v-icon.mdi-information-outline {
					color: $grey-300;
				}

				.pot-number {
					border-color: $grey-300;
				}
			}
		}

		.live-read {
			background: $ihm-red2;
			color: $ihm-white;
		}
	}
}

.v-expansion-panel.sortable-ghost {
	.asset-container {
		background: rgba(var(--eventTypeColorRGBValues), .17);
	}
}

.drop-target-indicator {
	background-color: $ihm-orange;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 4px;
	width: 100%;
	position: absolute;
	z-index: 2;
	transition: all 0.5s ease;
	bottom: 0;

	> .v-btn {
		background-color: #fff;
		color: $ihm-orange !important;
		margin: 0 10px;
	}
}

.modes-selector {
	--v-input-padding-top: 0;

	:deep(.v-field) {
		font-size: 16px;
		font-weight: bold;
	}

	:deep(.v-field__outline::before),
	:deep(.v-field--focused .v-field__outline::after) {
		border-width: 0;
	}
}

@keyframes pulse {
	0% {
		opacity: 100%;
	}
	50% {
		opacity: 30%;
	}
	100% {
		opacity: 100%;
	}
}

.cueingAsNext {
	animation: pulse 1s infinite;
}
</style>